import React, { memo, useMemo } from 'react';
import pick from 'lodash/pick';
import { graphql, useFragment } from 'react-relay';
import type { groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow$key } from '@atlassian/jira-relay/src/__generated__/groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow.graphql';
import type {
	groupRow_issueTableGrouping_groupRow$data,
	groupRow_issueTableGrouping_groupRow$key,
} from '@atlassian/jira-relay/src/__generated__/groupRow_issueTableGrouping_groupRow.graphql';
import { useInlineCreateHandlers } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsItemExpanded } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import {
	getChildQueryVariablesForJql,
	type Column,
} from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-child-query-variables/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type {
	RenderChildRows,
	RenderRows,
	RenderCreateIssueController,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';

import {
	ASSIGNEE_TYPE,
	CATEGORY_TYPE,
	PRIORITY_TYPE,
	NUMBER_CF_TYPE,
	SPRINT_TYPE,
	STATUS_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { GroupContextFieldOptimisticResponse } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/optimistic-responses/types.tsx';
import { FirstGroupIssues } from './first-group-issues/index.tsx';
import { useInitialiseFirstGroupIssues } from './first-group-issues/utils.tsx';
import { GroupHeader } from './group-header/index.tsx';

export type BaseProps = {
	groupConnectionId: string;
	isVirtual: boolean;
	isGroupAtTop?: boolean;
	projectKey?: string | null;
	columns: Column[];
	renderChildRows?: RenderChildRows;
};

type FirstGroupRowProps = BaseProps & {
	firstGroupRow: groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow$key;
	loading: boolean;
	childItemsExperience: string;
	childItemsLoadMoreExperience: string;
	analyticsSource: string;
	pageSize: number;
	width: number;
	isIssueRankDescending: boolean;
	isHideDoneItemsEnabled: boolean;
	actionColumns: string[];
	amountOfColumns: number;
	renderRows: RenderRows;
	renderCreateIssueController: RenderCreateIssueController;
};

const getGroupFieldValue = (
	fieldValue: groupRow_issueTableGrouping_groupRow$data['fieldValue'],
): string | null => {
	switch (fieldValue?.__typename) {
		case 'JiraJqlPriorityFieldValue':
			return fieldValue?.priority?.priorityId ?? null;
		case 'JiraJqlUserFieldValue':
			return fieldValue?.user?.accountId ?? null;
		case 'JiraJqlStatusFieldValue':
			return fieldValue?.status?.id ?? null;
		case 'JiraJqlNumberFieldValue':
			return fieldValue?.number?.toString() ?? null;
		case 'JiraJqlOptionFieldValue':
			return fieldValue?.option?.optionId ?? null;
		case 'JiraJqlSprintFieldValue':
			return fieldValue?.sprint?.sprintId ?? null;
		case 'JiraJqlGoalsFieldValue':
			return fieldValue?.goal?.id ?? null;
		default:
			return null;
	}
};

export const getGroupContextFieldOptimisticResponse = (
	fieldType: string,
	fieldValue: groupRow_issueTableGrouping_groupRow$data['fieldValue'],
): GroupContextFieldOptimisticResponse | undefined => {
	switch (fieldType) {
		case PRIORITY_TYPE:
			if (fieldValue?.__typename === 'JiraJqlPriorityFieldValue' && fieldValue?.priority) {
				return {
					type: PRIORITY_TYPE,
					priority: {
						id: fieldValue.priority.priorityId,
						name: fieldValue.priority.name,
						iconUrl: fieldValue.priority.iconUrl,
					},
				};
			}
			break;
		case ASSIGNEE_TYPE:
			return {
				type: ASSIGNEE_TYPE,
				user:
					fieldValue?.__typename === 'JiraJqlUserFieldValue' && fieldValue?.user
						? {
								id: fieldValue.user.accountId,
								accountId: fieldValue.user.accountId,
								name: fieldValue.user.name,
								picture: fieldValue.user.picture,
							}
						: null,
			};
		case STATUS_TYPE:
			if (
				fieldValue?.__typename === 'JiraJqlStatusFieldValue' &&
				fieldValue?.status &&
				fieldValue?.status.statusCategory
			) {
				return {
					type: STATUS_TYPE,
					status: {
						id: fieldValue.status.id,
						name: fieldValue.status.name,
						statusId: fieldValue.status.id,
						statusCategory: {
							statusCategoryId: fieldValue.status.statusCategory.statusCategoryId,
							id: fieldValue.status.statusCategory.id,
						},
					},
				};
			}
			break;
		case CATEGORY_TYPE:
			return {
				type: CATEGORY_TYPE,
				option:
					fieldValue?.__typename === 'JiraJqlOptionFieldValue' && fieldValue?.option
						? {
								id: fieldValue.option.optionId,
								value: fieldValue.option.value,
								optionId: fieldValue.option.optionId,
								color: {
									colorKey: fieldValue.option.color?.colorKey,
									id: fieldValue.option.color?.id,
								},
							}
						: null,
			};
		case SPRINT_TYPE:
			return {
				type: SPRINT_TYPE,
				sprint:
					fieldValue?.__typename === 'JiraJqlSprintFieldValue' && fieldValue?.sprint
						? {
								id: fieldValue.sprint.sprintId,
								sprintId: fieldValue.sprint.sprintId,
								name: fieldValue.sprint.name,
								state: fieldValue.sprint.state,
								endDate: fieldValue.sprint.endDate,
							}
						: null,
			};
		case STORY_POINT_ESTIMATE_CF_TYPE:
		case NUMBER_CF_TYPE:
			return {
				type: NUMBER_CF_TYPE,
				value: fieldValue?.__typename === 'JiraJqlNumberFieldValue' ? fieldValue.number : null,
			};
		default:
			return undefined;
	}
};

export const FirstGroupRow = ({
	firstGroupRow,
	groupConnectionId,
	isVirtual,
	loading,
	isGroupAtTop,
	projectKey = null,
	columns,
	actionColumns,
	amountOfColumns,
	isHideDoneItemsEnabled,
	pageSize,
	childItemsExperience,
	childItemsLoadMoreExperience,
	analyticsSource,
	width,
	isIssueRankDescending,
	renderRows,
	renderCreateIssueController,
}: FirstGroupRowProps) => {
	const cloudId = useCloudId();
	const firstGroupRowData =
		useFragment<groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow$key>(
			graphql`
				fragment groupRow_issueTableGrouping_FirstGroupRow_firstGroupRow on JiraSpreadsheetGroup
				@argumentDefinitions(
					isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
					isReparentingEnabled: { type: "Boolean", defaultValue: false }
					isDensityFull: { type: "Boolean", defaultValue: false }
					projectKeys: { type: "[String!]" }
					projectKey: { type: "String" }
				) {
					id
					jql @required(action: THROW)
					fieldValue {
						__typename
						... on JiraJqlPriorityFieldValue {
							priority {
								id
								priorityId
								name
								iconUrl
							}
						}
						... on JiraJqlUserFieldValue {
							user {
								id
								accountId
								name
								picture
							}
						}
						... on JiraJqlStatusFieldValue {
							status {
								id
								name
								statusCategory {
									id
									statusCategoryId
								}
							}
						}
						... on JiraJqlNumberFieldValue {
							number
						}
						... on JiraJqlOptionFieldValue {
							option {
								id
								optionId
								value
								color {
									colorKey
									id
								}
							}
						}
						... on JiraJqlSprintFieldValue {
							sprint {
								id
								sprintId
								name
								state
								endDate
							}
						}
						... on JiraJqlGoalsFieldValue {
							goal {
								id
							}
						}
					}
					...groupHeader_issueTableGrouping
					issues(fieldSetsInput: $fieldSetsInput, first: 50)
						@connection(key: "firstGroupSection_nativeIssueTable__issues") {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							__typename
						}
						__id
						# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
						...childRowsPagination_issueTableHierarchy_queryData
							@arguments(
								isInlineEditingEnabled: $isInlineEditingEnabled
								isReparentingEnabled: $isReparentingEnabled
								isDensityFull: $isDensityFull
								projectKeys: $projectKeys
								projectKey: $projectKey
								shouldQueryHasChildren: false
							)
					}
				}
			`,
			firstGroupRow,
		);

	const variables = useMemo(() => {
		return {
			...getChildQueryVariablesForJql<Column>({
				cloudId,
				columns,
				jql: firstGroupRowData.jql,
				isReparentingEnabled: false,
				actionColumns,
				amountOfColumns,
				pageSize,
				isHideDoneItemsEnabled,
			}),
			shouldQueryFieldSetsById: false,
			namespace: 'ISSUE_NAVIGATOR',
			shouldQueryHasChildren: false,
		};
	}, [
		cloudId,
		columns,
		firstGroupRowData.jql,
		actionColumns,
		amountOfColumns,
		pageSize,
		isHideDoneItemsEnabled,
	]);

	const baseProps = ['cloudId', 'issueSearchInput', 'fieldSetsInput'] as const;
	const propsWithViewConfigInput = [...baseProps, 'viewConfigInput'] as const;

	const hasInitialised = useInitialiseFirstGroupIssues({
		groupId: firstGroupRowData.id,
		groupConnectionId,
		firstGroupItemsConnectionId: firstGroupRowData.issues?.__id ?? null,
		issueSearchVariables: pick(
			variables,
			...(isHideDoneItemsEnabled ? propsWithViewConfigInput : baseProps),
		),
		loading,
	});

	const [isExpanded] = useIsItemExpanded({
		itemId: firstGroupRowData.id,
		groupId: firstGroupRowData.id,
		itemsConnectionId: groupConnectionId,
	});

	let inlineCreateHandlers;
	if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		inlineCreateHandlers = useInlineCreateHandlers({
			projectKey,
		});
	}

	return (
		<>
			{isVirtual && (
				<GroupHeader
					groupHeader={firstGroupRowData}
					groupConnectionId={groupConnectionId}
					isGroupAtTop={isGroupAtTop}
					createChildTriggerProps={inlineCreateHandlers?.triggerProps}
					columnsCount={columns.length}
					groupId={firstGroupRowData.id}
				/>
			)}
			{hasInitialised && isExpanded && (
				<FirstGroupIssues
					variables={variables}
					groupId={firstGroupRowData.id}
					groupConnectionId={groupConnectionId}
					groupFieldValue={getGroupFieldValue(firstGroupRowData.fieldValue)}
					formProps={inlineCreateHandlers?.formProps}
					isFormVisible={inlineCreateHandlers?.isFormVisible}
					pageSize={pageSize}
					width={width}
					isIssueRankDescending={isIssueRankDescending}
					childItemsExperience={childItemsExperience}
					childItemsLoadMoreExperience={childItemsLoadMoreExperience}
					analyticsSource={analyticsSource}
					renderRows={renderRows}
					renderCreateIssueController={renderCreateIssueController}
				/>
			)}
		</>
	);
};

type GroupRowProps = BaseProps & { groupRow: groupRow_issueTableGrouping_groupRow$key };

export const GroupRow = ({
	groupRow,
	groupConnectionId,
	isVirtual,
	projectKey = null,
	columns,
	renderChildRows,
}: GroupRowProps) => {
	const groupRowData = useFragment<groupRow_issueTableGrouping_groupRow$key>(
		graphql`
			fragment groupRow_issueTableGrouping_groupRow on JiraSpreadsheetGroup {
				id
				jql @required(action: THROW)
				fieldId
				fieldType
				fieldValue {
					__typename
					... on JiraJqlPriorityFieldValue {
						priority {
							id
							priorityId
							name
							iconUrl
						}
					}
					... on JiraJqlUserFieldValue {
						user {
							id
							accountId
							name
							picture
						}
					}
					... on JiraJqlStatusFieldValue {
						status {
							id
							name
							statusCategory {
								id
								statusCategoryId
							}
						}
					}
					... on JiraJqlNumberFieldValue {
						number
					}
					... on JiraJqlOptionFieldValue {
						option {
							id
							optionId
							value
							color {
								colorKey
								id
							}
						}
					}
					... on JiraJqlSprintFieldValue {
						sprint {
							id
							sprintId
							name
							state
							endDate
						}
					}
					... on JiraJqlGoalsFieldValue {
						goal {
							id
						}
					}
				}
				...groupHeader_issueTableGrouping
			}
		`,
		groupRow,
	);

	let inlineCreateHandlers;
	let groupContextField;
	const groupFieldValue = getGroupFieldValue(groupRowData.fieldValue);
	if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		inlineCreateHandlers = useInlineCreateHandlers({
			projectKey,
		});
		const { fieldId, fieldType } = groupRowData;
		groupContextField =
			fieldId && fieldType
				? {
						fieldValue: groupFieldValue,
						fieldId,
						fieldType,
						optimisticResponse: getGroupContextFieldOptimisticResponse(
							fieldType,
							groupRowData.fieldValue,
						),
					}
				: undefined;
	}

	return (
		groupRowData && (
			<>
				{isVirtual && (
					<GroupHeader
						groupHeader={groupRowData}
						groupConnectionId={groupConnectionId}
						createChildTriggerProps={inlineCreateHandlers?.triggerProps}
						columnsCount={columns.length}
						groupId={groupRowData.id}
					/>
				)}
				{renderChildRows?.({
					parentItemId: groupRowData.id,
					itemsConnectionId: groupConnectionId,
					depth: 0,
					type: 'GROUP_CHILDREN',
					groupFieldValue,
					groupJql: groupRowData.jql,
					formProps: inlineCreateHandlers?.formProps,
					isFormVisible: inlineCreateHandlers?.isFormVisible,
					groupId: fg('jira_spreadsheet_issue_create_in_group_targeting_')
						? groupRowData.id
						: undefined,
					groupContextField,
				})}
			</>
		)
	);
};

export const GroupRowWithMemo = memo(GroupRow);
export const FirstGroupRowWithMemo = memo(FirstGroupRow);
